/**
 * PartnersContactSupport — iter98 rewrite
 *
 * Spec change: removed the "Schedule a meeting" card. Replaced with a
 * direct contact form (Subject + Message) that hits POST
 * /api/partners/support/tickets — same backend used by Submit Ticket but
 * with a simpler 2-field shape. Modern + scalable.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail,
  Clock,
  ShieldCheck,
  Send,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import SEO from '../../components/SEO';
import PartnerAccountLayout from '../../components/PartnerAccountLayout';
import { API, authHeaders } from './account/_utils';

const inputBase =
  'w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/45 text-[14px] focus:outline-none focus:border-gold-500/50 transition-colors';
const inputError =
  'w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-red-500/60 text-white placeholder-white/45 text-[14px] focus:outline-none focus:border-red-500 transition-colors';

export default function PartnersContactSupport() {
  const [form, setForm] = useState({ subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { ticket_ref }

  const submit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (!form.subject.trim()) e.subject = 'Required';
    if (form.message.trim().length < 10) e.message = 'Please write at least 10 characters';
    setErrors(e);
    if (Object.keys(e).length) return;

    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/partners/support/tickets`,
        {
          subject: form.subject.trim(),
          message: form.message.trim(),
          category: 'general',
          priority: 'normal',
        },
        { headers: authHeaders() }
      );
      setSubmitted({ ticket_ref: data?.ticket_ref || data?.id || 'received' });
      setForm({ subject: '', message: '' });
      toast.success('Message sent — we will reply within 24 business hours');
    } catch (err) {
      const d = err?.response?.data?.detail || 'Could not send';
      toast.error(typeof d === 'string' ? d : 'Could not send');
    } finally {
      setSending(false);
    }
  };

  return (
    <PartnerAccountLayout
      currentKey="contact"
      title="Contact Support"
      subtitle="Send us a message and we will reply within 24 business hours."
    >
      <SEO
        title="Contact Support — NoFee Partners"
        description="Get in touch with the NoFee Partnership Team."
        path="/partners/support/contact"
        noindex
      />

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 lg:gap-8">
        {/* Contact form */}
        <form
          onSubmit={submit}
          className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 lg:p-8"
          data-testid="contact-support-form"
        >
          <h2 className="text-lg font-bold text-white mb-1">Send us a message</h2>
          <p className="text-xs text-white/45 mb-6">
            For account-specific issues. Typically answered within 24 business hours.
          </p>

          {submitted ? (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.05] p-6 text-center" data-testid="contact-support-success">
              <CheckCircle2 className="w-8 h-8 text-emerald-300 mx-auto mb-3" />
              <p className="text-base font-bold text-white">Message received</p>
              <p className="text-xs text-white/60 mt-1">
                Reference: <span className="font-mono text-emerald-300">{submitted.ticket_ref}</span>
              </p>
              <p className="text-xs text-white/55 mt-3 leading-relaxed">
                We will reply to your registered email within 24 business hours.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(null)}
                className="mt-5 inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-gold-500/30 hover:border-gold-500/60 bg-white/[0.02] text-gold-200 font-bold text-xs transition-all"
                data-testid="contact-support-new"
              >
                Send another message
              </button>
            </div>
          ) : (
            <>
              <label className="block mb-5">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-2">
                  Subject <span className="text-gold-400">*</span>
                </span>
                <input
                  value={form.subject}
                  onChange={(e) => { setForm({ ...form, subject: e.target.value }); setErrors({ ...errors, subject: undefined }); }}
                  placeholder="What can we help you with?"
                  className={errors.subject ? inputError : inputBase}
                  maxLength={160}
                  data-testid="contact-support-subject"
                />
                {errors.subject && <p className="mt-1.5 text-xs text-red-400">{errors.subject}</p>}
              </label>

              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-2">
                  Message <span className="text-gold-400">*</span>
                </span>
                <textarea
                  value={form.message}
                  onChange={(e) => { setForm({ ...form, message: e.target.value }); setErrors({ ...errors, message: undefined }); }}
                  rows={8}
                  placeholder="Describe your question or issue in as much detail as possible. Include screenshots or relevant links if helpful."
                  className={errors.message ? inputError : inputBase}
                  maxLength={5000}
                  data-testid="contact-support-message"
                />
                {errors.message && <p className="mt-1.5 text-xs text-red-400">{errors.message}</p>}
                <p className="mt-1.5 text-[11px] text-white/40">{form.message.length} / 5000</p>
              </label>

              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 disabled:opacity-50 text-black font-extrabold text-sm whitespace-nowrap transition-all"
                  data-testid="contact-support-submit"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Message
                </button>
              </div>
            </>
          )}
        </form>

        {/* Side panel — direct contact info */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6" data-testid="contact-email-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center text-gold-300">
                <Mail className="w-4.5 h-4.5" />
              </div>
              <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-gold-300">Email Support</p>
            </div>
            <a
              href="mailto:partners@nofee.restaurant"
              className="text-[15px] font-bold text-white hover:text-gold-300 transition-colors"
              data-testid="contact-email"
            >
              partners@nofee.restaurant
            </a>
            <p className="text-xs text-white/55 mt-2">Typically within 24 business hours.</p>
          </div>

          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6" data-testid="contact-hours-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center text-gold-300">
                <Clock className="w-4.5 h-4.5" />
              </div>
              <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-gold-300">Business Hours</p>
            </div>
            <p className="text-[15px] font-bold text-white">Mon–Fri · 9am–6pm ET</p>
            <p className="text-xs text-white/55 mt-2">Messages received outside business hours are answered the next business day.</p>
          </div>

          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6" data-testid="contact-security-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center text-gold-300">
                <ShieldCheck className="w-4.5 h-4.5" />
              </div>
              <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-gold-300">Security</p>
            </div>
            <p className="text-[14px] font-bold text-white">Never share passwords.</p>
            <p className="text-xs text-white/55 mt-2">NoFee will never ask for your password or Stripe credentials.</p>
          </div>
        </div>
      </div>
    </PartnerAccountLayout>
  );
}
